import { useEffect, useRef, useState } from "react";
import SubstanceApi from "../api/LIMS/Substance";
import { ValidationStatus, ValidationTextField } from "./ValidationTextField";
import { useDebounce } from "../hooks/useDebounce";
import { Substance } from "../api/types";
import { TextFieldProps } from "@mui/material";

interface ValidateChemIdTextFieldProps {
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
  onStatusChange?: (status: ValidationStatus) => void;
  onSubstanceChange?: (substance: Substance | null) => void;
  debounceMs?: number;
  minChars?: number;
  label?: string;
  allowInactiveChemIds?: boolean;
  disabled?: boolean;
  inputProps?: TextFieldProps["inputProps"];
}

export const ValidateChemIdTextField = ({
  placeholder = "",
  value,
  onChange,
  onStatusChange,
  onSubstanceChange,
  debounceMs = 500,
  minChars = 2,
  label = "* Chem ID",
  allowInactiveChemIds = false,
  disabled = false,
  ...rest
}: ValidateChemIdTextFieldProps) => {
  const [status, setStatus] = useState<ValidationStatus>("idle");
  const [helperText, setHelperText] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const [substance, setSubstance] = useState<Substance | null>(null);

  const debouncedValue = useDebounce(value, debounceMs);

  useEffect(() => {
    onStatusChange?.(status);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    onSubstanceChange?.(substance);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [substance]);

  useEffect(() => {
    if (!debouncedValue || debouncedValue.trim().length < minChars) {
      setStatus("idle");
      setHelperText(debouncedValue ? `Type at least ${minChars} characters` : "");
      abortRef.current?.abort();
      return;
    }

    const clearSubstance = () => setSubstance(null);

    const validateChemId = async () => {
      setStatus("loading");
      setHelperText("Checking…");

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      SubstanceApi.getByChemID(debouncedValue.trim(), allowInactiveChemIds, false)
        .then((response) => {
          if (response && response.message === "Success" && response.result) {
            setStatus("valid");
            setHelperText("Valid Chem ID");
            setSubstance(response.result);
          } else {
            setStatus("invalid");
            setHelperText("Invalid Chem ID");
            clearSubstance();
          }
        })
        .catch((error) => {
          if (error.name === "AbortError") return;
          setStatus("error");
          setHelperText("Lookup failed");
          clearSubstance();
        });
    };

    validateChemId();
  }, [allowInactiveChemIds, debouncedValue, minChars]);

  return (
    <ValidationTextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      variant="outlined"
      helperText={helperText}
      disabled={disabled}
      size="small"
      margin="normal"
      InputLabelProps={{ shrink: true }}
      fullWidth
      status={status}
      {...rest}
    />
  );
};
